"use client"
import { useAppStore } from "@/app/(store)/appStore"
import { useSyncStore } from "@/app/(store)/sync.store"
import { BlueActionButton } from "@/components/generic/BlueActionButton"
import { ButtonWithMenu } from "@/components/generic/ButtonWithMenu"
import { MenuItemChangeAvailabilityButton } from "@/components/generic/HeaderNavBar/components/HeaderNavButton/components/HeaderButtonProfile/components/MenuItemChangeAvailabilityButton"
import { MenuItemUserData } from "@/components/generic/HeaderNavBar/components/HeaderNavButton/components/HeaderButtonProfile/components/MenuItemUserData"
import { UserProfileImageWithUserStatus } from "@/components/generic/HeaderNavBar/components/HeaderNavButton/components/HeaderButtonProfile/components/UserProfileImageWithUserStatus"
import { CallerIDSelector } from "@/features/webphone/CallerIDSelector"
import { useActionCenterNotifications } from "@/hooks/useActionCenterNotifications"
import { cn } from "@/lib/utils"
import {
  ActionIcon,
  Anchor,
  Badge,
  Box,
  Card,
  Checkbox,
  Divider,
  Image,
  Menu,
  MenuDivider,
  MenuDropdown,
  MenuTarget,
  Skeleton,
  Text,
  Title,
  Tooltip,
} from "@mantine/core"
import { useElementSize } from "@mantine/hooks"
import { makeURL } from "@nauverse/make-url"
import {
  IconExternalLink,
  IconExternalLinkOff,
  IconLayoutBottombarCollapse,
  IconLayoutBottombarExpand,
  IconLayoutSidebarRightCollapse,
  IconLayoutSidebarRightExpand,
  IconPhone,
} from "@tabler/icons-react"
import Link from "next/link"
import { useEffect, useMemo } from "react"
import { useDynamoStore } from "@/app/(store)/dynamo.store"
import { prettifyPhone } from "@/lib/prettify.utils"

const styleVariants = {
  root: {
    popup: "h-screen w-screen",
    expanded: "size-full border-l border-gray-300",
    collapsed:
      "absolute right-4 bottom-0 w-[320px] border-l border-gray-300 border-t border-r rounded-t-md shadow-[0_-8px_30px_rgb(0,0,0,0.03)] hover:shadow-[0_-8px_30px_rgb(0,0,0,0.08)]",
  },
}

const HEADER_HEIGHT = 64

interface WebPhoneProps {
  classNames?: {
    root?: string
  }
  isPopup?: boolean
}

// Override the setting to use the old webphone
const _old_webphone = true

export function WebPhone({ classNames, isPopup }: WebPhoneProps) {
  const { ref: webphoneContainerRef, height, width } = useElementSize()
  const {
    webphoneExpanded: expanded,
    webphonePoppedOut: poppedOut,
    webphoneSelectedContactID: selectedContactID,
    // _old_webphone,
    setWebphonePoppedOut,
    setWebphoneExpanded,
    setWebphonePopupSize,
  } = useAppStore()

  const { userStatus, cluster } = useSyncStore()
  const { sendNotification } = useActionCenterNotifications()
  const { recentUserContacts, isFetchingRecentUserContacts } = useDynamoStore()
  const isLoadingContact =
    !selectedContactID || isFetchingRecentUserContacts !== "idle"

  function togglePopup() {
    setWebphonePoppedOut(!poppedOut)
  }

  useEffect(() => {
    if (isPopup) {
      window.onbeforeunload = () => {
        setWebphonePoppedOut(false)
        alert("Webphone popup closed")
      }
    }

    return () => {
      if (isPopup) {
        window.onbeforeunload = null
      }
    }
  }, [isPopup, setWebphonePoppedOut])

  useEffect(() => {
    if (isPopup) return

    setWebphonePopupSize({
      width: width ?? 420,
      height: height ?? 600,
    })
  }, [height, isPopup, setWebphonePopupSize, width])

  const selectedContact = useMemo(() => {
    if (!selectedContactID) return undefined

    return recentUserContacts.get(selectedContactID)
  }, [selectedContactID, recentUserContacts])

  return (
    <Box
      className={cn(
        "overflow-hidden bg-slate-50/75",
        isPopup
          ? styleVariants.root.popup
          : expanded
            ? styleVariants.root.expanded
            : styleVariants.root.collapsed,
        classNames?.root
      )}
      style={{
        borderLeftStyle: isPopup ? "none" : "solid",
      }}
    >
      <Box
        className={cn(
          "flex w-full flex-row items-center justify-between border-b border-gray-300 bg-white px-3",
          !expanded ? "cursor-pointer justify-center" : ""
        )}
        style={{
          height: HEADER_HEIGHT,
          borderBottomStyle: "solid",
        }}
        onClick={() => {
          if (isPopup || expanded) return

          setWebphoneExpanded(true)
        }}
      >
        <div className="flex flex-row items-center justify-start gap-1">
          {isPopup && (
            <Menu
              width={200}
              position="bottom-start"
              withArrow
              arrowPosition="center"
              offset={3}
            >
              <MenuTarget>
                <BlueActionButton
                  size="lg"
                  classNames={{
                    root: "!p-0 !border-0",
                    icon: "!p-0 !m-0",
                  }}
                >
                  <UserProfileImageWithUserStatus
                    fullName={userStatus?.fullName ?? ""}
                    email={userStatus?.email ?? ""}
                    classNames={{
                      avatar: "ml-0",
                    }}
                    indicatorOffset={6}
                    avatarSize={34}
                  />
                </BlueActionButton>
              </MenuTarget>
              <MenuDropdown className="!rounded-lg !shadow-lg">
                <MenuItemUserData />
                <MenuDivider />
                <MenuItemChangeAvailabilityButton />
              </MenuDropdown>
            </Menu>
          )}
          <Tooltip label={`${expanded ? "Collapse" : "Expand"} the webphone`}>
            <ActionIcon
              variant="subtle"
              size="lg"
              c="gray"
              style={{
                display: isPopup || !expanded ? "none" : "block",
              }}
              onClick={() => setWebphoneExpanded(!expanded)}
            >
              {expanded ? (
                <IconLayoutBottombarCollapse className="size-4" />
              ) : (
                <IconLayoutBottombarExpand className="size-4" />
              )}
            </ActionIcon>
          </Tooltip>
          <Tooltip
            label={`${poppedOut ? "Close the" : "Open a"} web phone popup`}
          >
            <ActionIcon
              variant="subtle"
              size="md"
              c="gray"
              onClick={togglePopup}
              style={{
                display: isPopup || !expanded ? "none" : "block",
              }}
            >
              {poppedOut ? (
                <IconExternalLinkOff className="size-4" />
              ) : (
                <IconExternalLink className="size-4" />
              )}
            </ActionIcon>
          </Tooltip>
        </div>
        <CallerIDSelector
          classNames={{
            root: !expanded ? "hidden" : "",
          }}
        />
        <Text
          style={{
            display: expanded ? "none" : "block",
          }}
        >
          {"{"}
          {selectedContactID ?? "None"}
          {"}"} - {"{"}Status
          {"}"}
        </Text>
      </Box>
      {_old_webphone ? (
        <Box
          ref={webphoneContainerRef}
          className="size-full"
          style={{
            height: expanded ? `calc(100% - ${HEADER_HEIGHT}px)` : 0,
          }}
        >
          <iframe
            src={makeURL(
              // Since it is available only in newtesting2 for now (manifest v3)
              // override the URL to use it
              "https://newtesting2.ringio.com/launcher-app",
              // cluster?.launcherUrlS ?? ""
              "chromeExtension",
              {
                params: {
                  chromeManifestVersion: 3,
                },
              }
            )}
            sandbox="allow-forms allow-scripts allow-popups allow-same-origin"
            allow="autoplay *;camera *;microphone *"
            className="size-full border-0"
          />
        </Box>
      ) : (
        <Box
          ref={webphoneContainerRef}
          className="flex flex-col items-center justify-start gap-4 p-3"
        >
          <Card
            padding={"md"}
            radius="md"
            shadow="sm"
            withBorder
            className="size-full !overflow-hidden bg-gradient-to-t from-[#E4F3E5] to-white !p-6"
            style={{
              background:
                "linear-gradient(0deg, rgba(228, 243, 229, 1) 0%, rgba(255, 255, 255, 1) 100%)",
            }}
          >
            {/* Contact Name, CRM and Score */}
            <Box className="mb-2 grid grid-cols-[auto_118px] grid-rows-1 items-center justify-between gap-3">
              {isLoadingContact ? (
                <Skeleton
                  className={"mt-1"}
                  height={32}
                  radius="xl"
                  width={180}
                  style={{
                    alignSelf: "start",
                    justifySelf: "start",
                  }}
                />
              ) : (
                <Title
                  order={3}
                  style={{
                    alignSelf: "start",
                    justifySelf: "start",
                  }}
                >
                  {selectedContact?.ContactFullname}
                </Title>
              )}
              <Box
                className="grid grid-cols-[14px_auto] grid-rows-1 items-center justify-start gap-3 pt-2"
                style={{
                  alignSelf: "start",
                  justifySelf: "end",
                }}
              >
                <Tooltip label={"Open this contact in the Zoho CRM"}>
                  <Link target="_blank" href="https://www.zoho.com/crm/">
                    <Image h={14} src="/assets/crm/zoho.png" alt="Zoho CRM" />
                  </Link>
                </Tooltip>
                <Badge size="lg" color="orange" className="!capitalize">
                  Score: <span className="">1.7</span>
                </Badge>
              </Box>
            </Box>
            {/* Contact Phone and Timezone */}
            <Box className="mt-1 flex flex-row items-center justify-between">
              {isLoadingContact ? (
                <Skeleton
                  className={"my-1"}
                  height={21}
                  radius="xl"
                  width={120}
                />
              ) : (
                <Text size="md">{selectedContact?.ContactPhoneNumber}</Text>
              )}

              {/*<Text size="xs" fs="italic" c="dimmed">*/}
              {/*  Unknown timezone*/}
              {/*</Text>*/}
            </Box>
            {/* Default Contact Data */}
            <Box className="mt-2 space-y-2">
              {/* Job Title */}
              <Box className="flex flex-row items-center justify-between">
                {isLoadingContact ? (
                  <Skeleton
                    className={"my-1"}
                    height={21}
                    radius="xl"
                    width={130}
                  />
                ) : (
                  <Text size="md" fs="italic">
                    Unknown title
                  </Text>
                )}
              </Box>
              {/* Company */}
              <Box className="flex flex-row items-center justify-between">
                {isLoadingContact ? (
                  <Skeleton
                    className={"my-1"}
                    height={21}
                    radius="xl"
                    width={100}
                  />
                ) : (
                  <Text size="md">
                    {selectedContact?.ContactCompanyName ?? "No company"}
                  </Text>
                )}
              </Box>
              {/* E-mail */}
              <Box className="flex flex-row items-center justify-between">
                {isLoadingContact ? (
                  <Skeleton
                    className={"my-1"}
                    height={21}
                    radius="xl"
                    width={150}
                  />
                ) : (
                  <Tooltip label={"Send an e-mail to this address"}>
                    <Text
                      c="blue"
                      td="underline"
                      size="md"
                      className="cursor-pointer"
                    >
                      {selectedContact?.ContactEmail ?? "No email"}
                    </Text>
                  </Tooltip>
                )}
              </Box>
            </Box>
            <Divider my="md" />
            {/* Top 5 Custom Contact Properties */}
            <Box className="space-y-2">
              <Box className="flex flex-row items-center justify-between">
                <Text size="md">Onboarded?</Text>
                <Text size="md" fw={500}>
                  In Process
                </Text>
              </Box>
              <Box className="flex flex-row items-center justify-between">
                <Text size="md">Hot lead?</Text>
                <Checkbox
                  // checked={webphone.tmp}
                  // onChange={(e) =>
                  //   changeWebphonePopupTmp(e.currentTarget.checked)
                  // }
                  size="sm"
                />
              </Box>
              <Box className="flex flex-row items-center justify-between">
                <Text size="md">LinkedIn Profile</Text>
                <Anchor
                  href={"https://linkedin.com"}
                  target="_blank"
                  underline="always"
                >
                  John Doe
                </Anchor>
              </Box>
              <Box className="flex flex-row items-center justify-between">
                <Text size="md">Website</Text>
                <Anchor
                  href={"https://example.com"}
                  target="_blank"
                  underline="always"
                >
                  example.com
                </Anchor>
              </Box>
              <Box className="flex flex-row items-center justify-between">
                <Text size="md">Last Call Date</Text>
                <Text size="md" fw={500}>
                  02-13-2024 at 10:30 am
                </Text>
              </Box>
            </Box>
          </Card>
          {!isLoadingContact && selectedContact?.ContactPhoneNumber && (
            <ButtonWithMenu
              buttonProps={{
                leftSection: <IconPhone className="size-5" />,
                fullWidth: true,
                classNames: {
                  root: "!h-12",
                  inner: "!justify-between !px-3",
                },
                size: "sm",
              }}
              onClick={() =>
                sendNotification("Test Notification", {
                  body: "This is a test notification",
                })
              }
              menuDropdownItems={[
                <Menu.Label key="label">
                  Other available phone numbers:
                </Menu.Label>,
                ...((selectedContact?.ContactPhoneNumbers ?? "") as string)
                  .split(",")
                  .filter((i) => i !== selectedContact.ContactPhoneNumber)
                  .map((phoneNumber) => {
                    return (
                      <Menu.Item
                        key={`phone-${phoneNumber}`}
                        onClick={(e) => {
                          e.stopPropagation()
                          console.log("123-456-7890 • Mobile Phone")
                        }}
                      >
                        {prettifyPhone({
                          phone: phoneNumber,
                        })}{" "}
                        • Mobile Phone
                      </Menu.Item>
                    )
                  }),
              ]}
            >
              <Text size="md">
                Call{" "}
                {prettifyPhone({
                  phone: selectedContact.ContactPhoneNumber,
                })}{" "}
                • Work Phone
              </Text>
            </ButtonWithMenu>
          )}
        </Box>
      )}
    </Box>
  )
}
